import { ALLOWED_IMAGE_MIME_TYPES, type AdminVideoDetail } from "@coinfallsmp/shared";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { creatorOf, useAuth } from "../AuthContext.js";
import {
  deleteVideo,
  finalizeUpload,
  getThumbnailUploadUrl,
  getVideoDetail,
  publishVideo,
  unpublishVideo,
  updateVideo,
  uploadToPresignedUrl,
} from "../api.js";
import CreatorBadge from "../components/CreatorBadge.js";
import StatusBadge from "../components/StatusBadge.js";
import PublishTargetsPanel from "../components/PublishTargetsPanel.js";
import VideoPreview from "../components/VideoPreview.js";

export default function VideoDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { identity } = useAuth();
  const isAdmin = creatorOf(identity)?.isAdmin ?? false;
  const [video, setVideo] = useState<AdminVideoDetail | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    const detail = await getVideoDetail(id);
    setVideo(detail);
    setTitle(detail.title);
    setDescription(detail.description ?? "");
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // While an import job is still running, poll so status flips automatically.
  useEffect(() => {
    if (!video || video.status !== "processing") return;
    const interval = setInterval(load, 4000);
    return () => clearInterval(interval);
  }, [video, load]);

  if (!video || !id) return <p className="status-message">Loading…</p>;

  async function saveMetadata() {
    setBusy(true);
    setError(null);
    try {
      await updateVideo(id!, { title, description: description || null });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setBusy(false);
    }
  }

  async function onPublishToggle() {
    setBusy(true);
    setError(null);
    try {
      if (video!.status === "published") await unpublishVideo(id!);
      else await publishVideo(id!);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update");
    } finally {
      setBusy(false);
    }
  }

  async function onDelete() {
    if (!confirm(`Permanently delete "${video!.title}"? This cannot be undone.`)) {
      return;
    }
    setBusy(true);
    try {
      await deleteVideo(id!);
      navigate("/");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete");
      setBusy(false);
    }
  }

  async function onThumbnailChange(file: File | null) {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const { uploadUrl, key } = await getThumbnailUploadUrl(id!, {
        contentType: file.type as (typeof ALLOWED_IMAGE_MIME_TYPES)[number],
        fileSizeBytes: file.size,
      });
      await uploadToPresignedUrl(uploadUrl, file);
      await finalizeUpload(id!, { kind: "thumbnail", key });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Thumbnail upload failed");
    } finally {
      setBusy(false);
    }
  }

  const canPublish = video.sourceType === "youtube" || Boolean(video.r2Key && video.fileSizeBytes);

  return (
    <section className="form-page">
      <div className="page-header">
        <h1>{video.title}</h1>
        <StatusBadge status={video.status} />
      </div>

      {video.uploader ? (
        <p className="form-hint">
          Uploaded by{" "}
          {isAdmin ? (
            <Link to={`/creators/${video.uploader.id}`}>
              <CreatorBadge badge={video.uploader.badge} /> {video.uploader.displayName}
            </Link>
          ) : (
            <>
              <CreatorBadge badge={video.uploader.badge} /> {video.uploader.displayName}
            </>
          )}
        </p>
      ) : null}

      {video.status === "published" ? (
        <p className="form-hint">
          {video.viewsCount} view{video.viewsCount === 1 ? "" : "s"} ·{" "}
          {video.likesCount} like{video.likesCount === 1 ? "" : "s"}
        </p>
      ) : null}

      {video.status === "processing" ? (
        <p className="form-hint">
          Normalizing the upload to a browser-playable format — this page
          refreshes automatically. See the <a href="/jobs">Jobs</a> log for
          details.
        </p>
      ) : null}

      {video.status === "failed" ? (
        <p className="form-error">
          This video failed to process. Check the Jobs log for the error, or
          delete it and try again.
        </p>
      ) : null}

      <VideoPreview
        videoId={id}
        ready={canPublish}
        sourceType={video.sourceType}
        sourceUrl={video.sourceUrl}
      />

      <div className="detail-grid">
        <div>
          <label>
            Title
            <input value={title} onChange={(e) => setTitle(e.target.value)} />
          </label>
          <label>
            Description
            <textarea
              rows={5}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>
          <label>
            Replace thumbnail
            <input
              type="file"
              accept={ALLOWED_IMAGE_MIME_TYPES.join(",")}
              onChange={(e) => onThumbnailChange(e.target.files?.[0] ?? null)}
              disabled={busy}
            />
          </label>

          {error ? <p className="form-error">{error}</p> : null}

          <div className="button-row">
            <button className="button primary" onClick={saveMetadata} disabled={busy}>
              Save changes
            </button>
            <button
              className="button secondary"
              onClick={onPublishToggle}
              disabled={busy || (!canPublish && video.status !== "published")}
              title={!canPublish ? "Upload finishes before you can publish" : undefined}
            >
              {video.status === "published" ? "Unpublish" : "Publish"}
            </button>
            <button className="button danger" onClick={onDelete} disabled={busy}>
              Delete
            </button>
          </div>
        </div>

        <div>
          <PublishTargetsPanel targets={video.publishTargets} />
        </div>
      </div>
    </section>
  );
}
